import { useMemo, useState } from "react";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useMember } from "@/contexts/memberContext";
import { isMemberASuperAdmin } from "@/core/auth";
import { useLocationsQuery } from "@/hooks/useLocationsQuery";
import { getLocationLabel, isCurrentSlug } from "@/lib/utils";
import {
  BellDot,
  BriefcaseBusiness,
  CalendarDays,
  ChartSpline,
  ChevronDown,
  ChevronRight,
  HelpCircle,
  MapPinCheck,
  MessagesSquare,
  ScrollText,
  ServerCog,
  Stamp,
  Terminal,
  Users,
  Wrench,
} from "lucide-react";
import { useMessagesEnabled } from "@/hooks/useMessagesEnabled";

type AdminSecondaryNavProps = {
  handleClick?: (url: string) => void;
  isExpanded: boolean;
  onToggle: () => void;
};

const AdminSecondaryNav = ({ handleClick, isExpanded, onToggle }: AdminSecondaryNavProps) => {
  const { open } = useSidebar();
  const messagesEnabled = useMessagesEnabled();
  const { member } = useMember();
  const isSuperAdmin = isMemberASuperAdmin(member);
  const { data: locations } = useLocationsQuery({ filters: {} });
  const [isDevToolsExpanded, setIsDevToolsExpanded] = useState(false);

  // Determine the calendar URL based on preferred location or first alphabetically
  const calendarUrl = useMemo(() => {
    if (!locations || locations.length === 0) {
      return "/location/bristol-clifton/calendar"; // Fallback if no locations loaded
    }

    const preferredLocationIds = member?.preferredLocations || [];

    // Try to use the first preferred location
    if (preferredLocationIds.length > 0) {
      const preferredLocation = locations.find((loc) => loc.id === preferredLocationIds[0]);
      if (preferredLocation?.slug) {
        return `/location/${preferredLocation.slug}/calendar`;
      }
    }

    // Fallback to the first location alphabetically (by label)
    const sortedLocations = [...locations]
      .filter((loc) => loc.active !== false)
      .sort((a, b) => getLocationLabel(a).localeCompare(getLocationLabel(b)));

    if (sortedLocations.length > 0 && sortedLocations[0].slug) {
      return `/location/${sortedLocations[0].slug}/calendar`;
    }

    return "/location/bristol-clifton/calendar"; // Final fallback
  }, [locations, member?.preferredLocations]);

  return (
    <SidebarGroup className="-ml-2.5 pr-0">
      {/* Administration header - collapsible */}
      {open && (
        <SidebarHeader
          className="text-sm flex justify-between flex-row ml-2 mr-0 px-1 bg-indigo-600 pl-2 rounded-lg hover:bg-indigo-800 cursor-pointer mb-1 text-white"
          onClick={onToggle}
        >
          <div className="flex items-center gap-2">
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <span>Administration</span>
          </div>
        </SidebarHeader>
      )}

      {isExpanded && (
        <div className="bg-indigo-200 px-1 ml-2 mr-0 rounded-lg">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip="View location calendars"
                  className={`hover:cursor-pointer hover:bg-indigo-600 hover:text-white ${
                    isCurrentSlug("/calendar") ? "bg-indigo-800 text-white" : ""
                  }`}
                  onClick={() => handleClick && handleClick(calendarUrl)}
                >
                  <CalendarDays />
                  <span>Calendars</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip="Manage all members"
                  className={`hover:cursor-pointer hover:bg-indigo-600 hover:text-white ${
                    isCurrentSlug("/admin/members") ? "bg-indigo-800 text-white" : ""
                  }`}
                  onClick={() => handleClick && handleClick("/admin/members")}
                >
                  <Users />
                  <span>Members</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip="Manage all bookings"
                  className={`hover:cursor-pointer hover:bg-indigo-600 hover:text-white ${
                    isCurrentSlug("/admin/bookings") ? "bg-indigo-800 text-white" : ""
                  }`}
                  onClick={() => handleClick && handleClick("/admin/bookings")}
                >
                  <Stamp />
                  <span>Bookings</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip="Manage all organisations"
                  className={`hover:cursor-pointer hover:bg-indigo-600 hover:text-white ${
                    isCurrentSlug("/admin/organisations") ? "bg-indigo-800 text-white" : ""
                  }`}
                  onClick={() => handleClick && handleClick("/admin/organisations")}
                >
                  <BriefcaseBusiness />
                  <span>Organisations</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip="Manage locations"
                  className={`hover:cursor-pointer hover:bg-indigo-600 hover:text-white ${
                    isCurrentSlug("/admin/locations") ? "bg-indigo-800 text-white" : ""
                  }`}
                  onClick={() => handleClick && handleClick("/admin/locations")}
                >
                  <MapPinCheck />
                  <span>Locations</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip="Create and manage notices for all locations"
                  className={`hover:cursor-pointer hover:bg-indigo-600 hover:text-white ${
                    isCurrentSlug("/admin/notices") ? "bg-indigo-800 text-white" : ""
                  }`}
                  onClick={() => handleClick && handleClick("/admin/notices")}
                >
                  <BellDot />
                  <span>Notices</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {/* <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip="View and manage finances"
                  className={`hover:cursor-pointer hover:bg-indigo-600 hover:text-white ${
                    isCurrentSlug("/admin/finances") ? "bg-indigo-800 text-white" : ""
                  }`}
                  onClick={() => handleClick && handleClick("/admin/finances")}
                >
                  <Wallet />
                  <span>Finances</span>
                </SidebarMenuButton>
              </SidebarMenuItem> */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip={`Reports and analytics`}
                  className={`hover:cursor-pointer hover:bg-indigo-600 hover:text-white ${
                    isCurrentSlug("/admin/reports") ? "bg-indigo-800 text-white" : ""
                  }`}
                  onClick={() => handleClick && handleClick("/admin/reports")}
                >
                  <ChartSpline />
                  <span>Reports</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip="Manage help content"
                  className={`hover:cursor-pointer hover:bg-indigo-600 hover:text-white ${
                    isCurrentSlug("/admin/help") ? "bg-indigo-800 text-white" : ""
                  }`}
                  onClick={() => handleClick && handleClick("/admin/help")}
                >
                  <HelpCircle />
                  <span>Help</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {messagesEnabled && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip="Audit every conversation in the system"
                    className={`hover:cursor-pointer hover:bg-indigo-600 hover:text-white ${
                      isCurrentSlug("/admin/messages") ? "bg-indigo-800 text-white" : ""
                    }`}
                    onClick={() => handleClick && handleClick("/admin/messages")}
                  >
                    <MessagesSquare />
                    <span>Messages</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {isSuperAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip="Manage settings"
                    className={`hover:cursor-pointer hover:bg-indigo-600 hover:text-white ${
                      isCurrentSlug("/admin/settings") ? "bg-indigo-800 text-white" : ""
                    }`}
                    onClick={() => handleClick && handleClick("/admin/settings")}
                  >
                    <ServerCog />
                    <span>Settings</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {isSuperAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip="Developer tools"
                    className="hover:cursor-pointer hover:bg-indigo-600 hover:text-white"
                    onClick={() => setIsDevToolsExpanded((v) => !v)}
                  >
                    {isDevToolsExpanded ? <ChevronDown /> : <ChevronRight />}
                    <Wrench />
                    <span>Dev Tools</span>
                  </SidebarMenuButton>
                  {isDevToolsExpanded && (
                    <SidebarMenu className="mt-1 border-l border-indigo-300 pl-2 min-w-0">
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          tooltip="View currently online members"
                          className={`hover:cursor-pointer hover:bg-indigo-600 hover:text-white ${
                            isCurrentSlug("/admin/dev-tools/online-members") ? "bg-indigo-800 text-white" : ""
                          }`}
                          onClick={() => handleClick && handleClick("/admin/dev-tools/online-members")}
                        >
                          <Users />
                          <span>Online members</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          tooltip="Live server log stream"
                          className={`hover:cursor-pointer hover:bg-indigo-600 hover:text-white ${
                            isCurrentSlug("/admin/dev-tools/logs") ? "bg-indigo-800 text-white" : ""
                          }`}
                          onClick={() => handleClick && handleClick("/admin/dev-tools/logs")}
                        >
                          <ScrollText />
                          <span>Logs</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          tooltip="Maintenance scripts (orphaned auth users, etc.)"
                          className={`hover:cursor-pointer hover:bg-indigo-600 hover:text-white ${
                            isCurrentSlug("/admin/dev-tools/tools") ? "bg-indigo-800 text-white" : ""
                          }`}
                          onClick={() => handleClick && handleClick("/admin/dev-tools/tools")}
                        >
                          <Terminal />
                          <span>Tools & Scripts</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </SidebarMenu>
                  )}
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </div>
      )}
    </SidebarGroup>
  );
};

export default AdminSecondaryNav;
